<script lang="ts">
  import { onMount } from "svelte";

  import {
    api,
    ApiError,
    type Entitlements,
    type PricingPlan,
    type PricingResponse,
  } from "../../lib/api";
  import { beginLogin, isSignedIn } from "../../lib/auth";
  import { commerceEnabled, resolveAppConfig } from "../../lib/config";
  import { openCheckout } from "../../lib/paddle";

  // Monthly ↔ yearly toggle. Both intervals must be configured in Paddle
  // (PADDLE_{PRO,MAX}_{MONTHLY,YEARLY}_PRICE_ID) for the toggle to show real
  // amounts on both sides; missing slots render as "Pricing soon".
  let interval = $state<"monthly" | "yearly">("monthly");
  let busy = $state("");
  let busyPortal = $state(false);
  let error = $state("");
  // In-app purchase is hidden on iOS (App Store guideline 3.1.1) and when the
  // cloud disables billing. Resolved once on mount.
  let commerce = $state(false);

  // Live pricing pulled from /v1/pricing on mount. ``null`` while loading;
  // ``{plans:[]}`` is a valid (but empty) "billing not yet configured"
  // response and we render plan cards without a number in that case.
  let pricing = $state<PricingResponse | null>(null);

  // Current user's entitlements — drives the per-card CTA (Upgrade / Current
  // / Downgrade / Switch). ``null`` for anonymous visitors (then every
  // paid card shows "Upgrade"; signing in is part of the upgrade flow).
  let ent = $state<Entitlements | null>(null);

  onMount(async () => {
    commerce = commerceEnabled(await resolveAppConfig());
    try {
      pricing = await api.pricing();
    } catch (e) {
      // Pricing endpoint failures are NOT user-facing — the cards just
      // render without amounts. The admin diagnostic surfaces the cause.
      pricing = { billing_enabled: false, currency: "USD", plans: [], errors: [] };
    }
    if (isSignedIn()) {
      try {
        ent = await api.entitlements();
      } catch {
        // Anonymous-equivalent if entitlements 401/500 — better than blocking
        // the whole page.
      }
    }
  });

  // Plan ordering for upgrade-vs-downgrade decisions. Mirrors the server's
  // _RANK in entitlement.py.
  const PLAN_RANK: Record<"basic" | "pro" | "max", number> = { basic: 0, pro: 1, max: 2 };

  /** What the button on a card should do, given the user's current plan AND
   *  the source of that plan. Critical distinction: "downgrade" is only
   *  meaningful when the user actually has a *paid* sub to cancel through
   *  Paddle — for group/manual grants there's nothing to manage in Paddle,
   *  so a lower-tier card shows "Included" instead of a button that 400s. */
  type CtaKind = "current" | "upgrade" | "downgrade" | "included" | "anon";
  function ctaFor(cardPlan: "basic" | "pro" | "max"): CtaKind {
    if (!ent) return "anon";
    if (ent.plan === cardPlan) return "current";
    if (PLAN_RANK[cardPlan] > PLAN_RANK[ent.plan]) {
      // Upgrades work regardless of source — even a group-granted user can
      // pay if they want (a paid sub then takes precedence per entitlement
      // priority). For manual grants you'd typically want admin coordination;
      // we still allow the click since the checkout itself is harmless.
      return "upgrade";
    }
    // Lower-rank card. Whether the user can act on it depends on source.
    return ent.source === "paid" ? "downgrade" : "included";
  }

  /** Short human label for non-paid sources, used in the current-plan card
   *  so "Max" without a Manage button doesn't read as broken UI. */
  function sourceLabel(s: Entitlements["source"]): string {
    switch (s) {
      case "group":  return "Granted by your access group";
      case "manual": return "Granted by your administrator";
      case "promo":  return "Promo access — limited time";
      case "free":   return "Free tier";
      default:       return "";
    }
  }

  /** Format the date in the user's locale (matches Account.svelte). */
  function fmtDate(iso: string | null): string {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString();
  }

  type Plan = {
    id: "basic" | "pro" | "max";
    name: string;
    blurb: string;
    features: string[];
    cta: "pro" | "max" | null;
  };

  // Non-quota features only. The monthly token allowance is rendered
  // separately from the live /v1/pricing `monthly_credits_by_plan` map
  // (see `tokenAllowance`), so the advertised number can't drift from the
  // server-side source of truth (plan_catalog).
  const PLANS: Plan[] = [
    {
      id: "basic",
      name: "Basic",
      blurb: "For getting started",
      features: ["Local Optio app", "Basic agent usage", "1 device"],
      cta: null,
    },
    {
      id: "pro",
      name: "Pro",
      blurb: "For professionals",
      features: ["Remote control", "Memory / RAG", "Advanced tools", "3 devices"],
      cta: "pro",
    },
    {
      id: "max",
      name: "Max",
      blurb: "For power users & teams",
      features: [
        "Advanced models",
        "Priority limits",
        "Advanced workflows",
        "10 devices",
      ],
      cta: "max",
    },
  ];

  /** Format a token count as a compact allowance label, e.g. 30_000_000 →
   *  "30M tokens / month". Falls back to a plain grouped number under 1M. */
  function formatTokens(n: number): string {
    if (n >= 1_000_000) return `${Math.round(n / 1_000_000)}M tokens / month`;
    if (n >= 1_000) return `${Math.round(n / 1_000)}K tokens / month`;
    return `${n.toLocaleString()} tokens / month`;
  }

  /** The token-allowance feature line for a plan, from the live pricing map.
   *  Returns null while pricing is loading or the map is absent, so the card
   *  simply omits the line rather than showing a stale literal. */
  function tokenAllowance(planId: string): string | null {
    const n = pricing?.monthly_credits_by_plan?.[planId];
    return typeof n === "number" ? formatTokens(n) : null;
  }

  /** Find the (plan, interval) row in the pricing response, if any. */
  function planPrice(planId: string, want: "monthly" | "yearly"): PricingPlan | undefined {
    return pricing?.plans.find((p) => p.plan === planId && p.interval === want);
  }

  /** Render a number into the plan's currency, e.g. "$19.00", "€19.00".
   *  Trims trailing ".00" so "$19/mo" looks clean. */
  function formatAmount(amount_decimal: string, currency: string): string {
    try {
      const n = Number(amount_decimal);
      const s = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        minimumFractionDigits: n % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(n);
      return s;
    } catch {
      return `${amount_decimal} ${currency}`;
    }
  }

  /** Human suffix: "/mo" / "/yr" (Paddle's cycle is the truth). */
  function cycleSuffix(p: PricingPlan): string {
    if (p.interval === "yearly") return "/yr";
    if (p.interval === "monthly") return "/mo";
    return "";
  }

  async function upgrade(plan: string) {
    error = "";
    if (!isSignedIn()) {
      const cfg = await resolveAppConfig();
      await beginLogin(cfg.oidc, "/pricing");
      return;
    }
    busy = plan;
    try {
      const cfg = await resolveAppConfig();
      const { transaction_id, client_token } = await api.checkout(plan, interval);
      await openCheckout(
        client_token || cfg.paddleClientToken,
        cfg.paddleEnvironment,
        transaction_id,
        `${window.location.origin}/success`,
      );
    } catch (e) {
      // Checkout 400 carries a useful detail (which env var is unset) — show it.
      error = e instanceof ApiError ? e.message : "Checkout failed";
    } finally {
      busy = "";
    }
  }

  /** Downgrade / cancel / switch interval all go through the Paddle customer
   *  portal — the cloud doesn't try to script those flows itself. The portal
   *  also handles billing-info edits and invoice history. */
  async function manage() {
    if (busyPortal) return;
    busyPortal = true;
    error = "";
    try {
      const { portal_url } = await api.portal();
      window.location.assign(portal_url);
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not open billing portal";
      busyPortal = false;
    }
  }
</script>

<section class="max-w-5xl mx-auto px-6 py-16">
  <h1 class="text-3xl font-bold text-center">Plans</h1>

  <!-- Monthly / Yearly toggle. Convention is yearly = 10× monthly, i.e. ~17%
       off (two months free). The "Save ~17%" label is static; the real saving
       is whatever Paddle returns — we don't compute it because Paddle's
       per-currency overrides may diverge. -->
  <div class="mt-6 flex justify-center">
    <div class="inline-flex rounded-full border border-white/10 bg-white/5 p-1 text-sm">
      <button
        class="px-4 py-1.5 rounded-full transition {interval === 'monthly' ? 'bg-indigo-500 text-white' : 'text-slate-300 hover:text-white'}"
        onclick={() => (interval = 'monthly')}
        aria-pressed={interval === 'monthly'}
      >Monthly</button>
      <button
        class="px-4 py-1.5 rounded-full transition flex items-center gap-1.5 {interval === 'yearly' ? 'bg-indigo-500 text-white' : 'text-slate-300 hover:text-white'}"
        onclick={() => (interval = 'yearly')}
        aria-pressed={interval === 'yearly'}
      >
        Yearly
        <span class="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-300">Save ~17%</span>
      </button>
    </div>
  </div>

  {#if error}
    <p class="mt-4 text-center text-red-400 text-sm">{error}</p>
  {/if}

  {#if ent && (ent.source === 'group' || ent.source === 'manual')}
    <!-- Granted access (Keycloak group / manual admin grant): the Paddle
         pricing surface isn't really applicable — the user already has
         their tier and there's no Paddle sub to manage. State this once at
         the top so each individual card's "Included" status reads as
         expected, not as a missing feature. -->
    <div class="mt-6 mx-auto max-w-2xl rounded-lg border border-indigo-400/30 bg-indigo-500/5 px-4 py-3 text-sm text-indigo-200 text-center">
      You have <span class="font-semibold capitalize">{ent.plan}</span> access — {sourceLabel(ent.source).toLowerCase()}. No paid subscription needed.
    </div>
  {/if}

  {#if ent?.scheduled_change_action === 'cancel' && ent?.scheduled_change_effective_at}
    <div class="mt-6 mx-auto max-w-2xl rounded-lg border border-amber-400/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200 flex items-center justify-between gap-3">
      <span>Your subscription cancels on {fmtDate(ent.scheduled_change_effective_at)}.</span>
      <button
        onclick={manage}
        disabled={busyPortal}
        class="px-3 py-1 rounded-md border border-amber-300/40 hover:bg-amber-400/10 disabled:opacity-50"
      >{busyPortal ? 'Opening…' : 'Reactivate'}</button>
    </div>
  {:else if ent?.scheduled_change_action === 'change_plan' && ent?.scheduled_change_effective_at}
    <div class="mt-6 mx-auto max-w-2xl rounded-lg border border-amber-400/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200 flex items-center justify-between gap-3">
      <span>Plan change scheduled for {fmtDate(ent.scheduled_change_effective_at)}.</span>
      <button
        onclick={manage}
        disabled={busyPortal}
        class="px-3 py-1 rounded-md border border-amber-300/40 hover:bg-amber-400/10 disabled:opacity-50"
      >{busyPortal ? 'Opening…' : 'Manage'}</button>
    </div>
  {:else if ent?.scheduled_change_action === 'pause' && ent?.scheduled_change_effective_at}
    <div class="mt-6 mx-auto max-w-2xl rounded-lg border border-amber-400/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200 flex items-center justify-between gap-3">
      <span>Subscription pauses on {fmtDate(ent.scheduled_change_effective_at)}.</span>
      <button
        onclick={manage}
        disabled={busyPortal}
        class="px-3 py-1 rounded-md border border-amber-300/40 hover:bg-amber-400/10 disabled:opacity-50"
      >{busyPortal ? 'Opening…' : 'Manage'}</button>
    </div>
  {/if}

  <div class="mt-8 grid gap-6 md:grid-cols-3">
    {#each PLANS as p (p.id)}
      {@const price = p.cta ? planPrice(p.cta, interval) : undefined}
      {@const cta = ctaFor(p.id)}
      <div
        class="rounded-xl border p-6 flex flex-col {cta === 'current'
          ? 'border-indigo-400 bg-indigo-500/5'
          : 'border-white/10'}"
      >
        <div class="flex items-center justify-between gap-2">
          <h2 class="text-xl font-semibold">{p.name}</h2>
          {#if cta === 'current'}
            <span class="rounded-full bg-indigo-500/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-indigo-300">Current plan</span>
          {/if}
        </div>
        <p class="text-slate-400 text-sm">{p.blurb}</p>
        {#if !p.cta}
          <!-- Basic / free tier: no Paddle price by design. -->
          <p class="mt-4 text-2xl font-bold">Free</p>
        {:else if pricing === null}
          <!-- Loading shimmer — keep the layout stable so the cards don't jump. -->
          <p class="mt-4 h-8 w-24 animate-pulse rounded bg-white/10"></p>
        {:else if price && !price.is_placeholder}
          <p class="mt-4 flex items-baseline gap-2">
            <span class="text-2xl font-bold">{formatAmount(price.amount_decimal, price.currency)}</span>
            <span class="text-sm text-slate-400">{cycleSuffix(price)}</span>
          </p>
        {:else if price && price.is_placeholder}
          <!-- Paddle's GET /prices isn't returning a real value (usually
               because the operator's API key lacks ``prices:read`` scope).
               Hide the demo number to avoid contradicting whatever Paddle
               actually charges at checkout — the real amount appears in the
               Paddle.js overlay regardless. -->
          <p class="mt-4 text-sm text-slate-400">Pricing shown at checkout</p>
        {:else}
          <p class="mt-4 text-sm text-slate-500">Pricing soon</p>
        {/if}
        <ul class="mt-4 space-y-1 text-sm text-slate-300 flex-1">
          {#if tokenAllowance(p.id)}
            <li class="font-medium text-slate-200">· {tokenAllowance(p.id)}</li>
          {/if}
          {#each p.features as f (f)}
            <li>· {f}</li>
          {/each}
        </ul>

        <!-- CTA region: matches the rules every modern subscription page
             uses — Current / Upgrade / Downgrade / Included — so the user
             always knows where they are vs each tier AND why. -->
        {#if cta === 'current'}
          {#if ent?.source === 'paid'}
            <button
              onclick={manage}
              disabled={busyPortal}
              class="mt-6 px-4 py-2 rounded-md border border-indigo-400/40 hover:border-indigo-400 text-indigo-200 disabled:opacity-50"
            >
              {busyPortal ? 'Opening…' : 'Manage subscription'}
            </button>
          {:else if ent}
            <!-- Non-paid current plan: explain WHY the user has access so
                 the absence of a Manage button doesn't look like a missing
                 feature. -->
            <span class="mt-6 text-center text-sm text-slate-400">{sourceLabel(ent.source)}</span>
          {/if}
        {:else if cta === 'included'}
          <!-- Lower-rank tier than what the user already has via a non-paid
               grant — they get this tier "for free" through their grant, so
               nothing to buy and nothing to downgrade to in Paddle. -->
          <span class="mt-6 text-center text-sm text-slate-500">Included in your access</span>
        {:else if !p.cta}
          <!-- Basic card, user is on a paid plan → offer a clean downgrade
               path via portal (cancel ends at period_end → back to basic). -->
          {#if cta === 'downgrade'}
            <button
              onclick={manage}
              disabled={busyPortal}
              class="mt-6 px-4 py-2 rounded-md border border-white/15 hover:border-white/30 text-slate-200 disabled:opacity-50"
            >
              {busyPortal ? 'Opening…' : 'Downgrade to Basic'}
            </button>
          {:else}
            <span class="mt-6 text-center text-sm text-slate-500">Included free</span>
          {/if}
        {:else if commerce && pricing?.checkout_configured}
          <!-- Paid card. Upgrade goes through checkout; Downgrade routes to
               the Paddle portal (changing tier in Paddle is the same
               operation as cancelling + resubscribing, so we don't try to
               script it ourselves). -->
          {#if cta === 'upgrade' || cta === 'anon'}
            <button
              onclick={() => upgrade(p.cta!)}
              disabled={busy === p.cta}
              class="mt-6 px-4 py-2 rounded-md bg-indigo-500 hover:bg-indigo-400 text-white disabled:opacity-50"
            >
              {busy === p.cta ? 'Opening checkout…' : `Upgrade to ${p.name}`}
            </button>
          {:else if cta === 'downgrade'}
            <button
              onclick={manage}
              disabled={busyPortal}
              class="mt-6 px-4 py-2 rounded-md border border-white/15 hover:border-white/30 text-slate-200 disabled:opacity-50"
            >
              {busyPortal ? 'Opening…' : `Downgrade to ${p.name}`}
            </button>
          {/if}
        {:else if commerce}
          <span class="mt-6 text-center text-sm text-amber-300/80">Checkout not configured yet</span>
        {:else}
          <span class="mt-6 text-center text-sm text-slate-500">Manage your plan on the web</span>
        {/if}
      </div>
    {/each}
  </div>
</section>
